import { all, includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { isRequired } from '@gef-ui/form-core/validations/validationFunctions/common';
import {
	acceptedExtensionDropzone,
	maxFileSizeDropzone,
	maxNumOfFiles,
	totalMaxFileSize,
} from '@gef-ui/form-core/validations/validationFunctions/fileUpload';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { isDateAfterSpecifiedDateField, isDateInFuture, isFullySpecifiedDate } from 'app/forms/validations/common';
import hasFileNameMaxLength from 'app/forms/validations/hasFileNameMaxLength';
import isBirthNumber from 'app/forms/validations/isBirthNumber';
import isBusinessIn from 'app/forms/validations/isBusinessIn';
import isBusinessName from 'app/forms/validations/isBusinessName';
import isClaimNumberCP from 'app/forms/validations/isClaimNumberCP';
import isEmail from 'app/forms/validations/isEmail';
import isFirstName from 'app/forms/validations/isFirstName';
import isLogin from 'app/forms/validations/isLogin';
import isMunicipality from 'app/forms/validations/isMunicipality';
import isNoteTaskCreating from 'app/forms/validations/isNoteTaskCreating';
import isNumber from 'app/forms/validations/isNumber';
import isPhoneNumber from 'app/forms/validations/isPhoneNumber';
import isPolicyNo from 'app/forms/validations/isPolicyNo';
import isProposalNo from 'app/forms/validations/isProposalNo';
import isSellerOrProposalNo from 'app/forms/validations/isSellerOrProposalNo';
import isStreet from 'app/forms/validations/isStreet';
import isStreetNumber from 'app/forms/validations/isStreetNumber';
import isSurname from 'app/forms/validations/isSurname';
import isTitle from 'app/forms/validations/isTitle';
import isZipCode from 'app/forms/validations/isZipCode';
import { MaxLengths } from 'constants/forms';
import { COMMON_FORM_FIELD_NAMES } from 'features/common/forms/constants';
import { radioOptionsIds } from 'features/other/resolverSettings/constants';
import {
	taskTypeBackoffice,
	taskTypeContactClient,
	taskTypeMobileScan,
	taskTypeSellerRequest,
} from 'features/views/taskCreatingPages/constants';

export const SELLER_REQUEST_FORM_NAME = 'sellerRequest-form';
export const BACKOFFICE_FORM_NAME = 'backOffice-form';
export const MOBILE_SCAN_FORM_NAME = 'mobileScan-form';
export const CONTACT_CLIENT_FORM_NAME = 'contactClient-form';
export const MANUAL_LEAD_FORM_NAME = 'manualLead-form';

const {
	firstName,
	surname,
	policyNumber,
	proposalNumber,
	businessName,
	policyStatus,
	title,
	businessIn,
	municipality,
	street,
	streetNo,
	zipCode,
	phoneNumber,
	email,
	birthDate,
	policyHolderFirstName,
	policyHolderSurname,
	policyHolderBusinessName,
	taskType,
	taskSubTypeId,
	note,
	superior,
	subordinate,
	agency,
	region,
	sellerIn,
	login,
	fileUpload,
	personIn,
} = COMMON_FORM_FIELD_NAMES;

export const FIELDS = {
	// detail info
	taskType,
	taskSubTypeId,
	fulfillDate: 'fulfillDate',
	warningDate: 'warningDate',
	additionalContactReason: 'additionalContactReason',

	// information PS
	policyNumber,
	proposalNumber,
	policyHolderFirstName,
	policyHolderSurname,
	policyHolderBusinessName,
	policyStatus,
	claimNoCP: 'claimNoCP',
	backOfficeSegment: 'backOfficeSegment',

	// clientInfo
	title,
	surname,
	firstName,
	businessName,
	businessIn,
	municipality,
	street,
	streetNo,
	zipCode,
	phoneNumber,
	email,
	birthDate,
	personIn,

	// fileAttachments
	fileUpload,

	// documentAttachment
	documentId: 'documentId',

	// note
	note,

	// resolverSettings
	radio: 'radio',
	superior,
	subordinate,
	agency,
	region,
	login,
	sellerIn,
	userFirstName: 'userFirstName',
	userSurname: 'userSurname',
};

export default () => ({
	// detail info section
	[FIELDS.taskType]: {
		deepPath: [FIELDS.taskType],
		validations: [isRequired()],
	},
	[FIELDS.taskSubTypeId]: {
		deepPath: [FIELDS.taskSubTypeId],
		validations: [isRequired()],
	},
	[FIELDS.fulfillDate]: {
		deepPath: [FIELDS.fulfillDate],
		dependentFields: [FIELDS.warningDate],
		validations: [
			isRequired({
				when: ({ formData }) =>
					includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeSellerRequest]),
			}),
			isFullySpecifiedDate({
				when: ({ formData }) =>
					includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeSellerRequest]),
			}),
			isDateInFuture(),
		],
	},
	[FIELDS.warningDate]: {
		deepPath: [FIELDS.warningDate],
		dependentFields: [FIELDS.fulfillDate],
		validations: [
			isRequired({
				when: ({ formData }) =>
					includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeSellerRequest]),
			}),
			isFullySpecifiedDate({
				when: ({ formData }) =>
					includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeSellerRequest]),
			}),
			isDateInFuture(),
			isDateAfterSpecifiedDateField({
				field: FIELDS.fulfillDate,
				fieldLocalization: getLabelMessage('taskCreatingPages:fulfillDate'),
			}),
		],
	},
	[FIELDS.additionalContactReason]: {
		deepPath: [FIELDS.additionalContactReason],
		validations: [],
	},

	// information PS section
	[FIELDS.policyNumber]: {
		deepPath: [FIELDS.policyNumber],
		validations: [isNumber({ customMessage: getLabelMessage('validation:allowedCharacters') }), isPolicyNo()],
	},
	[FIELDS.proposalNumber]: {
		deepPath: [FIELDS.proposalNumber],
		validations: [isProposalNo()],
	},
	[FIELDS.policyHolderFirstName]: {
		deepPath: [FIELDS.policyHolderFirstName],
		validations: [],
	},
	[FIELDS.policyHolderSurname]: {
		deepPath: [FIELDS.policyHolderSurname],
		validations: [],
	},
	[FIELDS.policyHolderBusinessName]: {
		deepPath: [FIELDS.policyHolderBusinessName],
		validations: [],
	},
	[FIELDS.policyStatus]: {
		deepPath: [FIELDS.policyStatus],
		validations: [],
	},
	[FIELDS.claimNoCP]: {
		deepPath: [FIELDS.claimNoCP],
		validations: [isClaimNumberCP()],
	},
	[FIELDS.backOfficeSegment]: {
		deepPath: [FIELDS.backOfficeSegment],
		validations: [
			isRequired({
				when: ({ formData }) => formData?.taskType === taskTypeBackoffice,
			}),
		],
	},

	// client info section
	[FIELDS.title]: {
		deepPath: [FIELDS.title],
		validations: [isTitle()],
	},
	[FIELDS.surname]: {
		deepPath: [FIELDS.surname],
		validations: [
			isRequired({
				when: ({ formData }) =>
					includes(formData?.taskType, [
						taskTypeBackoffice,
						taskTypeMobileScan,
						taskTypeSellerRequest,
						taskTypeContactClient,
					]) && isNilOrEmpty(formData?.businessName),
			}),
			isSurname(),
		],
	},
	[FIELDS.firstName]: {
		deepPath: [FIELDS.firstName],
		validations: [
			isRequired({
				when: ({ formData }) =>
					includes(formData?.taskType, [
						taskTypeBackoffice,
						taskTypeMobileScan,
						taskTypeSellerRequest,
						taskTypeContactClient,
					]) && isNilOrEmpty(formData?.businessName),
			}),
			isFirstName(),
		],
	},
	[FIELDS.businessName]: {
		deepPath: [FIELDS.businessName],
		validations: [
			isRequired({
				when: ({ formData }) =>
					includes(formData?.taskType, [
						taskTypeBackoffice,
						taskTypeMobileScan,
						taskTypeSellerRequest,
						taskTypeContactClient,
					]) && all(isNilOrEmpty)([formData?.firstName, formData?.surname]),
			}),
			isBusinessName(),
		],
	},
	[FIELDS.businessIn]: {
		deepPath: [FIELDS.businessIn],
		validations: [isBusinessIn()],
	},
	[FIELDS.municipality]: {
		deepPath: [FIELDS.municipality],
		validations: [
			isRequired({
				when: ({ formData }) =>
					includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeSellerRequest]),
			}),
			isMunicipality(),
		],
	},
	[FIELDS.street]: {
		deepPath: [FIELDS.street],
		validations: [isStreet()],
	},
	[FIELDS.streetNo]: {
		deepPath: [FIELDS.streetNo],
		validations: [isStreetNumber()],
	},
	[FIELDS.zipCode]: {
		deepPath: [FIELDS.zipCode],
		validations: [
			isRequired({
				when: ({ formData }) => {
					if (includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeSellerRequest])) {
						return true;
					} else {
						// excluding contactClient to get manualLead types only
						if (formData?.taskType !== taskTypeContactClient && formData?.radio === 'marketPlace') {
							return true;
						}
						return false;
					}
				},
			}),
			isZipCode(),
		],
	},
	[FIELDS.phoneNumber]: {
		deepPath: [FIELDS.phoneNumber],
		validations: [
			isRequired({
				when: ({ formData }) =>
					// contactClient type exception - only phoneNumber is required - email field is not relevant
					!includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan]) &&
					(formData?.taskType !== taskTypeContactClient ? isNilOrEmpty(formData?.email) : true),
			}),
			isPhoneNumber(),
		],
	},
	[FIELDS.email]: {
		deepPath: [FIELDS.email],
		validations: [
			isRequired({
				when: ({ formData }) =>
					!includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeContactClient]) &&
					isNilOrEmpty(formData?.phoneNumber),
			}),
			isEmail(),
		],
	},
	[FIELDS.birthDate]: {
		deepPath: [FIELDS.birthDate],
		validations: [isFullySpecifiedDate()],
	},
	[FIELDS.personIn]: {
		deepPath: [FIELDS.personIn],
		validations: [isBirthNumber()],
	},

	// fileAttachments section
	[FIELDS.fileUpload]: {
		deepPath: [FIELDS.fileUpload],
		validations: [
			hasFileNameMaxLength({
				fileNameMaxLength: MaxLengths.FILE_NAME,
			}),
			maxFileSizeDropzone({
				maxSize: 5000000,
			}),
			acceptedExtensionDropzone({
				extension: '.bmp,.doc,.docx,.gif,.htm,.html,.jpeg,.jpg,.pdf,.png,.rtf,.tif,.tiff,.txt,.xls,.xlsx,.xml',
			}),
			totalMaxFileSize({ maxSize: 30000000 }),
			maxNumOfFiles({ numOfFiles: MaxLengths.FILE_AMOUNT }),
		],
	},

	// documentAttachment section
	[FIELDS.documentId]: {
		deepPath: [FIELDS.documentId],
		validations: [],
	},

	// note section
	[FIELDS.note]: {
		deepPath: [FIELDS.note],
		validations: [isNoteTaskCreating()],
	},

	// radio section
	[FIELDS.radio]: {
		deepPath: [FIELDS.radio],
		validations: [isRequired()],
	},
	[FIELDS.superior]: {
		deepPath: [FIELDS.superior],
		validations: [
			isRequired({
				when: ({ formData }) => formData?.radio === radioOptionsIds.superior,
			}),
		],
	},
	[FIELDS.subordinate]: {
		deepPath: [FIELDS.subordinate],
		validations: [
			isRequired({
				when: ({ formData }) => formData?.radio === radioOptionsIds.subordinate,
			}),
		],
	},
	[FIELDS.agency]: {
		deepPath: [FIELDS.agency],
		validations: [isRequired({ when: ({ formData }) => formData?.radio === radioOptionsIds.agency })],
	},
	[FIELDS.region]: {
		deepPath: [FIELDS.region],
		validations: [isRequired({ when: ({ formData }) => formData?.radio === radioOptionsIds.agency })],
	},
	[FIELDS.login]: {
		deepPath: [FIELDS.login],
		validations: [
			isRequired({
				when: ({ formData }) =>
					formData?.radio === radioOptionsIds.users &&
					all(isNilOrEmpty)([formData?.sellerIn, formData?.userSurname]),
			}),
			isLogin(),
		],
	},
	[FIELDS.sellerIn]: {
		deepPath: [FIELDS.sellerIn],
		validations: [
			isRequired({
				when: ({ formData }) =>
					formData?.radio === radioOptionsIds.users &&
					all(isNilOrEmpty)([formData?.login, formData?.userSurname]),
			}),
			isSellerOrProposalNo(),
		],
	},
	[FIELDS.userFirstName]: {
		deepPath: [FIELDS.userFirstName],
		validations: [isFirstName()],
	},
	[FIELDS.userSurname]: {
		deepPath: [FIELDS.userSurname],
		validations: [
			isRequired({
				when: ({ formData }) =>
					formData?.radio === radioOptionsIds.users &&
					all(isNilOrEmpty)([formData?.login, formData?.sellerIn]),
			}),
			isSurname(),
		],
	},
});
